import {
   parseLocalizedAnswerRaw,
   toPositiveInt,
} from "./examQuestionFormat";

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function normalizeTicketCode(value) {
   const text = String(value ?? "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
   if (!text) return "";
   if (/^P?\d+$/.test(text)) {
      const digits = text.replace(/^P/, "");
      return `P${Number(digits)}`;
   }
   return text;
}

function readStringList(value) {
   if (!Array.isArray(value)) return [];
   return value.map((item) => String(item ?? "").trim()).filter(Boolean);
}

function readPermutation(value) {
   if (!Array.isArray(value) || !value.length) return null;
   const mapped = value.map((item) => Number(item));
   if (mapped.some((item) => !Number.isInteger(item) || item < 0)) return null;
   return mapped;
}

function readFinalAnswers(value) {
   if (!Array.isArray(value) || !value.length) return null;

   const mapped = value.map((item) => {
      if (typeof item === "string") {
         return parseLocalizedAnswerRaw(item);
      }
      if (isObject(item)) {
         return {
            ro: String(item?.ro ?? "").trim(),
            ru: String(item?.ru ?? "").trim(),
         };
      }
      return { ro: "", ru: "" };
   });

   if (!mapped.length || mapped.every((item) => !item.ro && !item.ru)) {
      return null;
   }

   return mapped;
}

function normalizeQuestionPatch(raw, ticketMeta = {}) {
   if (!isObject(raw)) return null;

   const questionId = toPositiveInt(
      raw?.questionId ?? raw?.question_id ?? raw?.id ?? raw?.qid,
   );
   if (!questionId) return null;

   const textRu = String(
      raw?.textRu ??
         raw?.text_ru ??
         raw?.ruText ??
         raw?.ru?.text ??
         raw?.text?.ru ??
         "",
   ).trim();

   const answersRu = readStringList(
      raw?.answersRu ??
         raw?.answers_ru ??
         raw?.ruAnswers ??
         raw?.ru?.answers ??
         raw?.answers?.ru,
   );

   const answersPermutation = readPermutation(
      raw?.ruOrderByRoIndex ??
         raw?.answersPermutation ??
         raw?.answers_perm ??
         raw?.permutation ??
         raw?.ruOrder ??
         raw?.ru_order ??
         raw?.answersRuOrder ??
         raw?.answers_ru_order,
   );

   const order = toPositiveInt(raw?.order ?? raw?.position ?? raw?.index);
   const bKey = String(raw?.b ?? raw?.B ?? raw?.questionKey ?? "").trim();
   const confidence = String(raw?.confidence ?? "").trim();
   const short = String(raw?.short ?? "").trim();
   const answerCount = toPositiveInt(raw?.answerCount ?? raw?.answer_count);
   const questionTextHint = String(
      raw?.questionText ?? raw?.question_text ?? "",
   ).trim();

   const finalAnswers =
      readFinalAnswers(raw?.finalAnswers ?? raw?.final_answers) ||
      readFinalAnswers(raw?.finalAnswerStrings ?? raw?.final_answer_strings);

   const patchKind = finalAnswers ? "text_repair" : "ru_reorder";

   return {
      questionId,
      ticketCode:
         ticketMeta.code || normalizeTicketCode(raw?.ticket) || "",
      ticketId: ticketMeta.ticketId || null,
      order,
      bKey,
      confidence,
      short,
      answerCount,
      questionTextHint,
      patchKind,
      textRu: textRu || null,
      answersRu: answersRu.length ? answersRu : null,
      answersPermutation,
      finalAnswers,
   };
}

function collectTicketQuestions(rawTicket, ticketMeta) {
   const directQuestions = safeList(
      rawTicket?.corrections ??
         rawTicket?.questions ??
         rawTicket?.items ??
         rawTicket?.entries ??
         rawTicket?.patch,
   );

   if (directQuestions.length) {
      return directQuestions
         .map((item) => normalizeQuestionPatch(item, ticketMeta))
         .filter(Boolean);
   }

   const single = normalizeQuestionPatch(rawTicket, ticketMeta);
   return single ? [single] : [];
}

function normalizeTicketPatch(raw, fallbackIndex = 0) {
   if (!isObject(raw)) return null;

   const ticketId = toPositiveInt(
      raw?.ticketId ?? raw?.ticket_id ?? raw?.id ?? raw?.biletId,
   );
   const ticketCode = normalizeTicketCode(
      raw?.code ??
         raw?.ticket ??
         raw?.label ??
         raw?.name ??
         raw?.ticketName ??
         raw?.ticket_code ??
         (ticketId ? `P${fallbackIndex + 1}` : ""),
   );

   const questionIds = safeList(raw?.questionIds ?? raw?.question_ids)
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0);

   const questions = collectTicketQuestions(raw, {
      code: ticketCode,
      ticketId,
   });

   if (!ticketId && !ticketCode && !questions.length && !questionIds.length) {
      return null;
   }

   return {
      ticketId,
      ticketCode,
      questionIds: questionIds.length ? questionIds : null,
      questions,
   };
}

function flattenTicketEntries(source) {
   if (Array.isArray(source)) {
      return source
         .map((item, index) => normalizeTicketPatch(item, index))
         .filter(Boolean);
   }

   if (!isObject(source)) return [];

   if (isObject(source?.patchesByTicket)) {
      return Object.entries(source.patchesByTicket)
         .map(([code, ticket], index) =>
            normalizeTicketPatch(
               {
                  ...(isObject(ticket) ? ticket : {}),
                  code: isObject(ticket) ? ticket?.code ?? code : code,
               },
               index,
            ),
         )
         .filter(Boolean);
   }

   const listKeys = ["tickets", "patches", "items", "data", "entries", "bilete"];
   for (const key of listKeys) {
      if (Array.isArray(source?.[key])) {
         return flattenTicketEntries(source[key]);
      }
   }

   if (isObject(source?.tickets) && !Array.isArray(source.tickets)) {
      return Object.entries(source.tickets)
         .map(([code, ticket], index) =>
            normalizeTicketPatch(
               {
                  ...(isObject(ticket) ? ticket : {}),
                  code: isObject(ticket) ? ticket?.code ?? code : code,
               },
               index,
            ),
         )
         .filter(Boolean);
   }

   const single = normalizeTicketPatch(source, 0);
   return single ? [single] : [];
}

export function parseTheoryRuReorderPatch(raw) {
   const errors = [];
   const tickets = flattenTicketEntries(raw);

   if (!tickets.length) {
      errors.push(
         "Fișierul nu conține patch-uri recunoscute. Verifică structura JSON (patchesByTicket / tickets).",
      );
   }

   const questionPatches = [];
   const ticketOrderPatches = [];

   for (const ticket of tickets) {
      if (ticket.questionIds?.length) {
         ticketOrderPatches.push({
            ticketId: ticket.ticketId,
            ticketCode: ticket.ticketCode,
            questionIds: ticket.questionIds,
         });
      }

      for (const question of ticket.questions) {
         if (
            !question.textRu &&
            !question.answersRu &&
            !question.answersPermutation &&
            !question.finalAnswers
         ) {
            errors.push(
               `${ticket.ticketCode || "?"} · #${question.questionId}: nu are reordonare RU (ruOrderByRoIndex) sau texte finale (finalAnswers).`,
            );
            continue;
         }

         questionPatches.push({
            ...question,
            ticketId: question.ticketId || ticket.ticketId || null,
            ticketCode: question.ticketCode || ticket.ticketCode || "",
         });
      }
   }

   if (!questionPatches.length && !ticketOrderPatches.length && !errors.length) {
      errors.push("Patch-ul nu conține operații aplicabile.");
   }

   const summary = isObject(raw?.summary) ? raw.summary : null;

   for (const warning of safeList(raw?.warnings)) {
      if (!isObject(warning)) continue;
      const ticket = String(warning?.ticket ?? "").trim();
      const b = String(warning?.b ?? warning?.B ?? "").trim();
      const text = String(
         warning?.warning ?? warning?.message ?? warning?.text ?? "",
      ).trim();
      if (!text) continue;
      const prefix = [ticket, b].filter(Boolean).join(" · ");
      errors.push(prefix ? `${prefix}: ${text}` : text);
   }

   return {
      meta: isObject(raw?.meta) ? raw.meta : {},
      version: raw?.version ?? raw?.schemaVersion ?? raw?.schema ?? null,
      type: raw?.type ?? null,
      applyRule: raw?.applyRule ?? raw?.criticalRule ?? null,
      summary,
      tickets,
      questionPatches,
      ticketOrderPatches,
      errors,
   };
}

export function applyRuPatchToQuestion(currentQuestion, patch) {
   if (!currentQuestion?.id || !patch?.questionId) {
      throw new Error("Patch sau întrebare invalidă.");
   }

   if (Number(currentQuestion.id) !== Number(patch.questionId)) {
      throw new Error("Patch-ul nu corespunde întrebării curente.");
   }

   const answers = (Array.isArray(currentQuestion.answers)
      ? currentQuestion.answers
      : []
   ).map((answer) => ({
      ro: String(answer?.ro ?? ""),
      ru: String(answer?.ru ?? ""),
   }));

   let nextTextRu = String(currentQuestion.textRu ?? "");
   if (patch.textRu) {
      nextTextRu = patch.textRu;
   }

   if (patch.finalAnswers?.length) {
      if (patch.finalAnswers.length !== answers.length) {
         throw new Error(
            `Număr diferit de răspunsuri (${patch.finalAnswers.length} vs ${answers.length}).`,
         );
      }

      for (let index = 0; index < answers.length; index += 1) {
         const next = patch.finalAnswers[index];
         answers[index].ro = String(next?.ro ?? "");
         answers[index].ru = String(next?.ru ?? "");
      }
   } else if (patch.answersRu?.length) {
      if (patch.answersRu.length !== answers.length) {
         throw new Error(
            `Număr diferit de răspunsuri RU (${patch.answersRu.length} vs ${answers.length}).`,
         );
      }

      for (let index = 0; index < answers.length; index += 1) {
         answers[index].ru = patch.answersRu[index];
      }
   } else if (patch.answersPermutation?.length) {
      if (patch.answersPermutation.length !== answers.length) {
         throw new Error(
            `Permutarea RU nu corespunde numărului de răspunsuri (${patch.answersPermutation.length} vs ${answers.length}).`,
         );
      }

      const sourceRu = answers.map((answer) => answer.ru);
      for (let targetIndex = 0; targetIndex < answers.length; targetIndex += 1) {
         const sourceIndex = patch.answersPermutation[targetIndex];
         answers[targetIndex].ru = sourceRu[sourceIndex] ?? "";
      }
   }

   return {
      id: currentQuestion.id,
      textRo: String(currentQuestion.textRo ?? ""),
      textRu: nextTextRu,
      image: String(currentQuestion.image ?? ""),
      answers,
      correctAnswer: Number(currentQuestion.correctAnswer ?? 1),
      categoryId: currentQuestion.categoryId ?? null,
   };
}

export function buildQuestionPreviewDiff(currentQuestion, patch) {
   const before = {
      textRo: String(currentQuestion?.textRo ?? ""),
      textRu: String(currentQuestion?.textRu ?? ""),
      answers: (currentQuestion?.answers || []).map((answer) => ({
         ro: String(answer?.ro ?? ""),
         ru: String(answer?.ru ?? ""),
      })),
   };

   let after;
   try {
      after = applyRuPatchToQuestion(currentQuestion, patch);
   } catch (error) {
      return {
         before,
         after: null,
         changedAnswerIndexes: [],
         error: error?.message || "Nu s-a putut calcula previzualizarea.",
      };
   }

   const changedAnswerIndexes = [];
   const changedRoAnswerIndexes = [];

   before.answers.forEach((answer, index) => {
      const nextRo = String(after?.answers?.[index]?.ro ?? "");
      const nextRu = String(after?.answers?.[index]?.ru ?? "");
      if (String(answer?.ro ?? "") !== nextRo) changedRoAnswerIndexes.push(index);
      if (String(answer?.ru ?? "") !== nextRu) changedAnswerIndexes.push(index);
   });

   const textRuChanged =
      before.textRu !== String(after?.textRu ?? "") &&
      Boolean(patch?.textRu);

   return {
      before,
      after: {
         textRo: String(after.textRo ?? ""),
         textRu: String(after.textRu ?? ""),
         answers: after.answers.map((answer) => ({
            ro: String(answer?.ro ?? ""),
            ru: String(answer?.ru ?? ""),
         })),
      },
      changedAnswerIndexes,
      changedRoAnswerIndexes,
      textRuChanged,
      patchKind: patch?.patchKind || "ru_reorder",
      hasChanges:
         changedAnswerIndexes.length > 0 ||
         changedRoAnswerIndexes.length > 0 ||
         textRuChanged,
      error: null,
   };
}
